import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom"; // added Link
import { blogContent } from "./BlogData";
import styles from "./BlogPost.module.css";

export default function BlogPost() {
  const { slug } = useParams();
  const article = blogContent[slug];
  const [isVisible, setIsVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [contentLoaded, setContentLoaded] = useState(false);
  const headerRef = useRef(null);
  const imageRef = useRef(null);
  const contentRef = useRef(null);

  
  // Intersection Observer for header
  useEffect(() => {
    if (!article) return;
    
    const headerObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: "50px" }
    );

    if (headerRef.current) {
      headerObserver.observe(headerRef.current);
    }

    return () => headerObserver.disconnect();
  }, [article]);

  // Image lazy loading
  useEffect(() => {
    if (!article || !isVisible || !imageRef.current) return;
    
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.src = article.thumbnail;
  }, [isVisible, article]);

  // Content lazy loading
  useEffect(() => {
    if (!article || !isVisible || !contentRef.current) return;
    
    const timer = setTimeout(() => {
      setContentLoaded(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [isVisible, article]);
  
  if (!article) {
    return <div>Blog post not found.</div>;
  }
  
  return (
    <div className={styles.blogPage}>
      {/* Header */}
      <header 
        ref={headerRef}
        className={styles.blogHeader}
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(30px)",
          transition: "all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
        }}
      >
        <h1 className={styles.blogTitle}>{article.title}</h1>
        <div className={styles.blogMetaWrapper}>
          <div className={styles.blogMeta}>
            <span className={styles.blogAuthor}>By {article.author}</span>
            <span className={styles.blogDot}>•</span>
            <span className={styles.blogReadtime}>{article.readTime}</span>
          </div>
        </div>
      </header>
      
      {/* Featured Image */}
      <div 
        className={styles.featuredImageContainer}
        style={{
          opacity: imageLoaded ? 1 : 0,
          transform: imageLoaded ? "scale(1)" : "scale(0.95)",
          transition: "all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
        }}
      >
        {!imageLoaded && (
          <div 
            className={styles.imagePlaceholder}
            style={{
              width: "100%",
              height: "560px",
              backgroundColor: "#f0f0f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "12px",
              marginBottom: "2rem"
            }}
          >
            <div style={{ fontSize: "16px", color: "#666" }}>Loading image...</div>
          </div>
        )}
        
        <img
          ref={imageRef}
          src={article.thumbnail}
          alt={article.title}
          className={styles.featuredImage}
          style={{
            display: imageLoaded ? "block" : "none"
          }}
        />
      </div>
      
      {/* Blog Content */}
      <div 
        ref={contentRef}
        className={styles.blogPostWrapper}
        style={{
          opacity: contentLoaded ? 1 : 0,
          transform: contentLoaded ? "translateY(0)" : "translateY(40px)",
          transition: "all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
        }}
      >
        <article className={styles.blogArticle}>
          <div
            className={styles.blogHtmlContent}
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </article>

        {/* CTA Button under article */}
        <div style={{ marginTop: "3rem", textAlign: "center" }}>
          <Link to="/create-user">
            <button className={styles.ctaButton}>
              Join NeuHawk Today!
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}
